import 'reactflow/dist/style.css'

import dagre from 'dagre'
import React, { type FC, useCallback, useEffect, useMemo } from 'react'
import ReactFlow, {
  Background,
  Controls,
  type Edge,
  Handle,
  type Node,
  Position,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
} from 'reactflow'

import { type BrokersTask, type BrokersTaskTaskDependency } from 'utils/web_api.schemas'

import {
  getTaskContainerStyle,
  TaskStatusBadge,
} from './WorkflowVisualIndicators'

interface WorkflowDAGProps {
  tasks: BrokersTask[]
}

// Custom node component for tasks
const TaskNode: FC<{ data: { task: BrokersTask } }> = ({ data }) => {
  const { task } = data
  const containerStyle = getTaskContainerStyle(task)

  return (
    <div
      className={`${containerStyle} px-4 py-3 rounded-lg border-2 shadow-lg min-w-[200px] max-w-[250px] bg-white relative`}
    >
      {/* Connection handles for React Flow */}
      <Handle
        type='target'
        position={Position.Left}
        style={{
          background: '#3B82F6',
          width: 8,
          height: 8,
          border: '2px solid white',
        }}
      />
      <Handle
        type='source'
        position={Position.Right}
        style={{
          background: '#3B82F6',
          width: 8,
          height: 8,
          border: '2px solid white',
        }}
      />

      <div className='flex flex-col gap-2'>
        <div className='flex items-center justify-between gap-2'>
          <div className='text-sm font-semibold text-gray-900 truncate'>
            {task.display_name || task.task_type || 'Unknown Task'}
          </div>
          <TaskStatusBadge status={task.status} isExecutable={task.can_execute} />
        </div>

        {task.description && (
          <div className='text-xs text-gray-600 overflow-hidden' style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
            {task.description}
          </div>
        )}

        <div className='flex justify-between items-center text-xs text-gray-500'>
          <span>Seq: {task.sequence_order}</span>
          {task.fail_count > 0 && (
            <span className='text-red-600 font-medium'>
              Fails: {task.fail_count}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

const nodeTypes = {
  taskNode: TaskNode,
}

// Helper function to create a directed graph layout using dagre
const getLayoutElements = (
  tasks: BrokersTask[],
  dependencies: Array<{ source: string; target: string; type: string }>
) => {
  const dagreGraph = new dagre.graphlib.Graph()
  dagreGraph.setDefaultEdgeLabel(() => ({}))
  dagreGraph.setGraph({
    rankdir: 'LR', // Left to right layout
    align: 'UL',   // Align to upper left
    nodesep: 35,   // Vertical spacing between nodes (compact + breathing room)
    ranksep: 80,   // Horizontal spacing between ranks (compact)
  })

  // Create a set of valid task IDs for quick lookup
  const taskIds = new Set(tasks.map(task => task.id))

  // Create nodes for each task
  const nodes: Node[] = tasks.map((task) => {
    dagreGraph.setNode(task.id, { width: 250, height: 100 })

    return {
      id: task.id,
      type: 'taskNode',
      data: { task },
      position: { x: 0, y: 0 }, // Will be set by dagre
    }
  })

  // Filter dependencies to only include ones where both source and target tasks exist
  const validDependencies = dependencies.filter((dep) => {
    const sourceExists = taskIds.has(dep.source)
    const targetExists = taskIds.has(dep.target)
    const isValid = sourceExists && targetExists

    if (!isValid) {
      console.warn(`Skipping dependency: source ${dep.source} exists: ${sourceExists}, target ${dep.target} exists: ${targetExists}`)
    }
    return isValid
  })

  // Create edges from valid dependencies
  const edges: Edge[] = validDependencies.map((dep) => {
    dagreGraph.setEdge(dep.source, dep.target)

    return {
      id: `${dep.source}-${dep.target}`,
      source: dep.source,
      target: dep.target,
      animated: false,
      style: {
        stroke: dep.type === 'completion' ? '#3B82F6' : '#6B7280',
        strokeWidth: 2,
      },
      label: dep.type === 'completion' ? '' : dep.type,
      labelStyle: { fontSize: 10, fill: '#6B7280' },
    }
  })

  // Run dagre layout
  dagre.layout(dagreGraph)

  // Update node positions
  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id)

    return {
      ...node,
      position: {
        x: nodeWithPosition.x - nodeWithPosition.width / 2,
        y: nodeWithPosition.y - nodeWithPosition.height / 2,
      },
    }
  })

  return { nodes: layoutedNodes, edges }
}

// Helper function to extract dependencies from tasks
const extractDependencies = (
  tasks: BrokersTask[]
): Array<{ source: string; target: string; type: string }> => {
  const dependencies: Array<{ source: string; target: string; type: string }> = []

  tasks.forEach((task) => {
    if (task.dependencies && task.dependencies.length > 0) {
      task.dependencies.forEach((dep: BrokersTaskTaskDependency) => {
        dependencies.push({
          source: dep.depends_on_task_id,
          target: dep.task_id,
          type: dep.dependency_type || 'completion',
        })
      })
    }
  })

  return dependencies
}

const WorkflowDAGInner: FC<WorkflowDAGProps> = ({ tasks }) => {
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])

  // Extract dependencies and create layout
  const { layoutedNodes, layoutedEdges } = useMemo(() => {
    if (!tasks || tasks.length === 0) {
      return { layoutedNodes: [], layoutedEdges: [] }
    }

    const dependencies = extractDependencies(tasks)
    const { nodes: layoutNodes, edges: layoutEdges } = getLayoutElements(
      tasks,
      dependencies
    )

    return { layoutedNodes: layoutNodes, layoutedEdges: layoutEdges }
  }, [tasks])

  // Update nodes and edges when layout changes
  useEffect(() => {
    setNodes(layoutedNodes)
    setEdges(layoutedEdges)
  }, [layoutedNodes, layoutedEdges, setNodes, setEdges])

  const onConnect = useCallback(() => {
    // Disable manual connections since this is a read-only visualization
  }, [])

  if (!tasks || tasks.length === 0) {
    return (
      <div className='flex items-center justify-center h-64 text-gray-500'>
        No tasks to display in the workflow DAG
      </div>
    )
  }

  if (layoutedEdges.length === 0) {
    return (
      <div className='flex flex-col items-center justify-center h-64 text-gray-500 space-y-2'>
        <div>No task dependencies found</div>
        <div className='text-sm'>
          Tasks will be executed in sequence order: {tasks.map(t => t.sequence_order).sort((a, b) => a - b).join(' → ')}
        </div>
        {tasks.length > 0 && (
          <div className='text-xs text-gray-400 mt-2'>
            Available tasks: {tasks.map(t => t.display_name || t.task_type || 'Unknown').join(', ')}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className='h-96 w-full border border-gray-200 rounded-lg bg-gray-50'>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{
          padding: 0.2,
          minZoom: 0.5,
          maxZoom: 1.5,
        }}
        attributionPosition='bottom-left'
        proOptions={{ hideAttribution: true }}
      >
        <Background color='#f3f4f6' gap={20} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </div>
  )
}

export const WorkflowDAG: FC<WorkflowDAGProps> = ({ tasks }) => {
  return (
    <ReactFlowProvider>
      <WorkflowDAGInner tasks={tasks} />
    </ReactFlowProvider>
  )
}

export default WorkflowDAG
